import { createElement, useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LeaveDialog from "./LeaveDialog";

/**
 * Hlídání odchodu během běžící dávky (F7.8): navigace v adminu a zavření okna výběru fotek se
 * zeptají dialogem aplikace, zavření nebo obnovení karty dotazem prohlížeče (`beforeunload`).
 *
 * Admin běží na `BrowserRouter`, kde `useBlocker` ani `unstable_usePrompt` nejdou (jen datové
 * routery). Ovladač proto chytá klik na interní odkaz ve fázi capture na dokumentu (dřív než
 * kořen Reactu, `Link` klik nedostane) a `popstate` ve fázi capture na okně (dřív než
 * `handlePop` React Routeru, který je bez capture): adresu vrátí `history.go(-delta)`, router se
 * o pohybu nedozví. Index položky čte z `history.state.idx`, kam ho ukládá React Router.
 * Programová navigace (`navigate()` z kódu) se nechytá. `window.confirm` se nepoužívá.
 */

/** Čekající odchod, na který se dialog ptá. */
export type PendingLeave =
  | { kind: "link"; to: string } // pathname + search + hash cíle
  | { kind: "history"; delta: number } // Zpět/Vpřed o delta položek
  | { kind: "confirm" }; // confirmLeave() z okna výběru

/** Vnitřní tvar `confirm`: nese odpověď čekajícímu `confirmLeave()`. */
interface ConfirmPending {
  kind: "confirm";
  resolve(leave: boolean): void;
}

export interface LeaveGuardEnv {
  win: Pick<Window, "addEventListener" | "removeEventListener" | "location" | "history">;
  doc: Pick<Document, "addEventListener" | "removeEventListener">;
  navigate(to: string): void;
}

export type ClickLike = Pick<
  MouseEvent,
  "defaultPrevented" | "button" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey"
> & { target: EventTarget | null };

interface AnchorLike {
  getAttribute(name: string): string | null;
  hasAttribute(name: string): boolean;
}

/**
 * Cíl kliku, který odchází ze stránky v rámci adminu (`pathname + search + hash`), jinak
 * `null`: zpracovaný klik, jiné než levé tlačítko, modifikátor (nová karta, stránka zůstává),
 * cíl mimo `a[href]`, `target` jiný než `_self`, `download`, neplatná adresa, jiný origin
 * (odchod mimo admin hlídá `beforeunload`) a stejná cesta (změna query nebo kotvy stránku
 * neodmontuje, např. odkaz na detail fotky na stránce Média).
 */
export function clickLeaveTarget(
  e: ClickLike,
  loc: { origin: string; pathname: string; href: string },
): string | null {
  if (e.defaultPrevented || e.button !== 0) return null;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
  const target = e.target as { closest?(selector: string): Element | null } | null;
  if (!target || typeof target.closest !== "function") return null;
  const a = target.closest("a[href]") as AnchorLike | null;
  if (!a) return null;
  const linkTarget = a.getAttribute("target");
  if (linkTarget !== null && linkTarget !== "" && linkTarget !== "_self") return null;
  if (a.hasAttribute("download")) return null;
  let url: URL;
  try {
    url = new URL(a.getAttribute("href") ?? "", loc.href);
  } catch {
    return null;
  }
  if (url.origin !== loc.origin || url.pathname === loc.pathname) return null;
  return url.pathname + url.search + url.hash;
}

export interface LeaveGuard {
  setActive(active: boolean): void;
  /** Vymění funkci navigace z `env` (identita `useNavigate()` se mění s polohou). */
  setNavigate(navigate: (to: string) => void): void;
  syncIndex(): void;
  getPending(): PendingLeave | null;
  subscribe(listener: () => void): () => void;
  confirmLeave(): Promise<boolean>;
  stay(): void;
  go(): void;
}

/** Zachycený odkaz, na kterém „Odejít“ klik zopakuje (`HTMLElement.click()`). */
interface ReplayableAnchor {
  click(): void;
  readonly isConnected: boolean;
}

/**
 * Ovladač hlídání bez Reactu. `setActive(false)` posluchače odregistruje a `stay()` vyřeší
 * čekající `confirmLeave()`; další `setActive(true)` je znovu zaregistruje (StrictMode v dev
 * spustí úklid efektů a znovu efekty nad týmž ovladačem).
 */
export function createLeaveGuard(env: LeaveGuardEnv): LeaveGuard {
  const { win, doc } = env;
  let navigate = env.navigate;
  let active = false;
  let guardPath = "";
  let idx: number | null = null;
  /** Varianta `confirm` nese i odpověď čekajícímu `confirmLeave()`. */
  let pending: Exclude<PendingLeave, { kind: "confirm" }> | ConfirmPending | null = null;
  /**
   * Co udělat s příští `popstate`: `swallow` = návrat adresy po `history.go(-delta)`,
   * `pass` = odchod po „Odejít“ projde k routeru.
   */
  let nextPop: "swallow" | "pass" | null = null;
  /** Odkaz čekajícího odchodu `link`, nad kterým „Odejít“ zopakuje klik. */
  let linkAnchor: ReplayableAnchor | null = null;
  /** Běží opakovaný klik po „Odejít“: zachycení ho pustí k `Link` a jeho `onClick`. */
  let replaying = false;
  const listeners = new Set<() => void>();

  /** Index položky z `history.state.idx` jen jako celé číslo, jinak `null`. */
  const readIdx = (): number | null => {
    const state: unknown = win.history.state;
    const value =
      state !== null && typeof state === "object" ? (state as { idx?: unknown }).idx : undefined;
    return typeof value === "number" && Number.isInteger(value) ? value : null;
  };

  /** Vymění čekající odchod; opouštěný `confirm` dostane odpověď `answer`. */
  const settle = (next: typeof pending, answer: boolean) => {
    const left = pending;
    pending = next;
    if (next?.kind !== "link") linkAnchor = null;
    if (left?.kind === "confirm") left.resolve(answer);
    for (const listener of [...listeners]) listener();
  };

  const onBeforeUnload = (e: Event) => {
    e.preventDefault();
    // Chrome a Edge před verzí 119 `preventDefault()` neberou a dotaz ukážou jen s neprázdným
    // `returnValue` (prázdný řetězec je výchozí hodnota a nic nezmění).
    (e as BeforeUnloadEvent).returnValue = "leave";
  };

  const onClick = (e: Event) => {
    if (replaying) return;
    const click = e as unknown as ClickLike;
    const to = clickLeaveTarget(click, win.location);
    if (to === null) return;
    e.preventDefault();
    // V DOM zahrnuje i `stopPropagation`: klik nedojde ke kořeni Reactu ani k `Link`.
    e.stopImmediatePropagation();
    const target = click.target as { closest?(selector: string): Element | null } | null;
    const a = target?.closest?.("a[href]") as Partial<ReplayableAnchor> | null | undefined;
    settle({ kind: "link", to }, false);
    linkAnchor = a && typeof a.click === "function" ? (a as ReplayableAnchor) : null;
  };

  /**
   * „Odejít“ u odkazu: klik se zopakuje na původním odkazu, aby proběhl i jeho vlastní
   * `onClick` (např. zavření postranního menu) a navigoval `Link`. Posluchač v bublání na
   * dokumentu (po kořeni Reactu) doplní navigaci, když klik nikdo nezpracoval; bez odkazu
   * v DOM se naviguje přímo.
   */
  const leaveByLink = (a: ReplayableAnchor | null, to: string) => {
    if (!a?.isConnected) {
      navigate(to);
      return;
    }
    const fallback = (e: Event) => {
      if (e.defaultPrevented) return;
      e.preventDefault();
      navigate(to);
    };
    doc.addEventListener("click", fallback);
    replaying = true;
    try {
      a.click();
    } finally {
      replaying = false;
      doc.removeEventListener("click", fallback);
    }
  };

  const onPopState = (e: Event) => {
    const mode = nextPop;
    nextPop = null;
    if (mode === "swallow") {
      e.stopImmediatePropagation();
      return;
    }
    if (mode === "pass") return;
    const newIdx = readIdx();
    // Bez celého čísla (`replaceState({})` v `InboxPage` a `IntegrationsPage` dá routeru
    // `idx = NaN`) by `history.go(-NaN)` stránku znovu načetl: pohyb projde.
    if (idx === null || newIdx === null || newIdx === idx || win.location.pathname === guardPath) {
      idx = newIdx;
      return;
    }
    e.stopImmediatePropagation();
    const delta = newIdx - idx;
    nextPop = "swallow";
    win.history.go(-delta);
    settle({ kind: "history", delta }, false);
  };

  const register = () => {
    win.addEventListener("beforeunload", onBeforeUnload);
    doc.addEventListener("click", onClick, { capture: true });
    win.addEventListener("popstate", onPopState, { capture: true });
  };
  const unregister = () => {
    win.removeEventListener("beforeunload", onBeforeUnload);
    doc.removeEventListener("click", onClick, { capture: true });
    win.removeEventListener("popstate", onPopState, { capture: true });
  };

  return {
    setActive(next) {
      if (next === active) return;
      active = next;
      if (next) {
        guardPath = win.location.pathname;
        idx = readIdx();
        nextPop = null;
        register();
      } else {
        // Otevřený dialog zůstává; „Odejít“ pak projde bez zachycení.
        unregister();
      }
    },
    setNavigate(next) {
      navigate = next;
    },
    syncIndex() {
      idx = readIdx();
    },
    getPending: () => pending,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    confirmLeave() {
      if (!active) return Promise.resolve(true);
      return new Promise<boolean>((resolve) => settle({ kind: "confirm", resolve }, false));
    },
    stay() {
      settle(null, false);
    },
    go() {
      const leave = pending;
      const a = linkAnchor;
      settle(null, true);
      if (leave?.kind === "link") leaveByLink(a, leave.to);
      else if (leave?.kind === "history") {
        if (active) nextPop = "pass";
        win.history.go(leave.delta);
      }
    },
  };
}

/**
 * Hlídání odchodu během `active` (běžící dávka): `dialog` vykreslí stránka nebo okno výběru,
 * `confirmLeave()` volá okno výběru před zavřením (neaktivní hlídání → `true` hned). Hák smí
 * běžet jen pod routerem. Odkazy s `target="_blank"` nechytá.
 */
export function useLeaveGuard(active: boolean): {
  dialog: ReactNode;
  confirmLeave(): Promise<boolean>;
} {
  const navigate = useNavigate();
  const [guard] = useState(() => createLeaveGuard({ win: window, doc: document, navigate }));
  // Identita `navigate` se mění s polohou; ovladač vzniká jednou a dostane vždy poslední.
  useEffect(() => {
    guard.setNavigate(navigate);
  }, [guard, navigate]);

  useEffect(() => {
    guard.setActive(active);
    return () => guard.setActive(false);
  }, [guard, active]);
  // Odmontování: čekající `confirmLeave()` dostane `false` a dialog zmizí.
  useEffect(() => () => guard.stay(), [guard]);

  // `push` na stejné cestě (odkaz na detail fotky) `popstate` nevyvolá: index se srovná zde.
  const { key } = useLocation();
  useEffect(() => {
    guard.syncIndex();
  }, [guard, key]);

  const pending = useSyncExternalStore(guard.subscribe, guard.getPending, () => null);
  const dialog = pending
    ? createElement(LeaveDialog, { onStay: guard.stay, onGo: guard.go })
    : null;
  return { dialog, confirmLeave: guard.confirmLeave };
}
